import { MutableRefObject, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { getSocket } from "../utils/socketMiddleware";
import { noop } from "../utils/misc";
import type { RootState } from "../store";

type SocketHandlerSignature<T> = (data: T) => void;

/**
 * Listen for ALL WebSocket messages of a specific type
 * Triggers for both promise-based responses AND unsolicited messages
 *
 * Use this when you want to intercept all messages regardless of source
 * (useful for logging, debugging, or global state management)
 *
 * @param messageType - The message type to listen for (e.g., "uiUpdate", "getData")
 * @param handler - Callback function to handle the message data
 *
 * @example
 * useSocketEvent("uiUpdate", (data) => {
 *   console.log("Any uiUpdate message received:", data);
 *   // This triggers for both:
 *   // 1. Response to socketRequest("getData")
 *   // 2. Unsolicited server update
 * });
 *
 * @example
 * // Log all messages of a type
 * useSocketEvent("notification", (data) => {
 *   logToAnalytics("notification_received", data);
 * });
 */
export const useSocketEvent = <T = unknown>(
  messageType: string,
  handler: (data: T) => void
) => {
  const savedHandler: MutableRefObject<SocketHandlerSignature<T>> = useRef(noop);

  // Track connection status from Redux to re-subscribe when socket reconnects
  const socketConnected = useSelector((state: RootState) => state.ui.socketConnected);

  // Update the handler ref when it changes
  useEffect(() => {
    savedHandler.current = handler;
  }, [handler]);

  // Subscribe to ALL socket messages (re-subscribe when connection state changes)
  useEffect(() => {
    if (!socketConnected) {
      //console.log(`[useSocketEvent] Socket not connected, waiting for connection for type: "${messageType}"`);
      return;
    }

    const socket = getSocket();

    if (!socket) {
      console.warn(`[useSocketEvent] WebSocket not available for message type: "${messageType}"`);
      return;
    }

    console.log(`[useSocketEvent] ✅ Subscribing to ALL messages of type: "${messageType}"`);

    const eventListener = (event: MessageEvent) => {
      try {
        const message = JSON.parse(event.data);

        // Check if this message matches the type we're listening for
        if (message.type === messageType) {
          if (savedHandler.current) {
            console.log(`[useSocketEvent] 📨 Received message for type "${messageType}":`, message);
            savedHandler.current(message as T);
          }
        }
      } catch (error) {
        console.error(`[useSocketEvent] Failed to parse WebSocket message:`, error);
      }
    };

    socket.addEventListener("message", eventListener);

    // Cleanup: remove listener on unmount or when socket disconnects
    return () => {
      console.log(`[useSocketEvent] Unsubscribing from message type: "${messageType}"`);
      socket.removeEventListener("message", eventListener);
    };
  }, [messageType, socketConnected]); // Re-subscribe when connection status changes
};
